import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

const BUCKET = 'evidence-private';
const MAX_FILES_PER_EVENT = 10;
const MAX_TOTAL_BYTES = 200 * 1024 * 1024;

const allowedTypes: Record<string, { kind: MediaKind; maxBytes: number }> = {
  'image/jpeg': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'image/png': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'image/webp': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'image/gif': { kind: 'image', maxBytes: 10 * 1024 * 1024 },
  'audio/mpeg': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/mp3': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/wav': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/x-wav': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/mp4': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/x-m4a': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/aac': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/x-aac': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/vnd.dlna.adts': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/ogg': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/opus': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/flac': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'audio/webm': { kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  'video/mp4': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/webm': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/quicktime': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/mpeg': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/x-msvideo': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/x-matroska': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
  'video/3gpp': { kind: 'video', maxBytes: 100 * 1024 * 1024 },
};

type MediaKind = 'image' | 'audio' | 'video';

const extensionTypes: Record<string, { mime: string; kind: MediaKind; maxBytes: number }> = {
  jpg: { mime: 'image/jpeg', kind: 'image', maxBytes: 10 * 1024 * 1024 },
  jpeg: { mime: 'image/jpeg', kind: 'image', maxBytes: 10 * 1024 * 1024 },
  png: { mime: 'image/png', kind: 'image', maxBytes: 10 * 1024 * 1024 },
  webp: { mime: 'image/webp', kind: 'image', maxBytes: 10 * 1024 * 1024 },
  gif: { mime: 'image/gif', kind: 'image', maxBytes: 10 * 1024 * 1024 },
  mp3: { mime: 'audio/mpeg', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  wav: { mime: 'audio/wav', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  m4a: { mime: 'audio/mp4', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  aac: { mime: 'audio/aac', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  ogg: { mime: 'audio/ogg', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  opus: { mime: 'audio/opus', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  flac: { mime: 'audio/flac', kind: 'audio', maxBytes: 25 * 1024 * 1024 },
  mp4: { mime: 'video/mp4', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  webm: { mime: 'video/webm', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  mov: { mime: 'video/quicktime', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  mpeg: { mime: 'video/mpeg', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  mpg: { mime: 'video/mpeg', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  avi: { mime: 'video/x-msvideo', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  mkv: { mime: 'video/x-matroska', kind: 'video', maxBytes: 100 * 1024 * 1024 },
  '3gp': { mime: 'video/3gpp', kind: 'video', maxBytes: 100 * 1024 * 1024 },
};

export interface EvidenceMediaRecord {
  id: number;
  event_id: number;
  user_id: string;
  bucket_id: string;
  file_path: string;
  file_name: string;
  mime_type: string;
  media_type: MediaKind;
  size_bytes: number;
  description: string | null;
  created_at: string;
  signedUrl?: string;
}

interface Props {
  eventId: number;
  userId: string;
}

export default function EvidenceMediaManager({ eventId, userId }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [records, setRecords] = useState<EvidenceMediaRecord[]>([]);
  const [description, setDescription] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDescription, setEditingDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadMedia = useCallback(async () => {
    setError(null);
    const { data, error: queryError } = await supabase
      .from('practice_event_media')
      .select('*')
      .eq('event_id', eventId)
      .order('created_at', { ascending: true });

    if (queryError) {
      setError(`無法載入附件：${queryError.message}`);
      return;
    }

    const withUrls = await Promise.all(
      ((data || []) as EvidenceMediaRecord[]).map(async (record) => {
        const { data: signedData, error: signedError } = await supabase.storage
          .from(BUCKET)
          .createSignedUrl(record.file_path, 60 * 60);

        return {
          ...record,
          signedUrl: signedError ? undefined : signedData.signedUrl,
        };
      }),
    );

    setRecords(withUrls);
  }, [eventId]);

  useEffect(() => {
    void loadMedia();
  }, [loadMedia]);

  const handleFiles = async (files: FileList | null) => {
    if (!files?.length || busy) return;

    setError(null);
    setMessage(null);

    const selected = Array.from(files);
    if (records.length + selected.length > MAX_FILES_PER_EVENT) {
      setError(`每筆紀錄最多 ${MAX_FILES_PER_EVENT} 個附件，目前已有 ${records.length} 個。`);
      return;
    }

    const existingBytes = records.reduce((sum, record) => sum + Number(record.size_bytes), 0);
    const selectedBytes = selected.reduce((sum, file) => sum + file.size, 0);
    if (existingBytes + selectedBytes > MAX_TOTAL_BYTES) {
      setError('每筆紀錄的附件總容量不可超過 200 MB。');
      return;
    }

    for (const file of selected) {
      const rule = getFileRule(file);
      if (!rule) {
        setError(`不支援「${file.name}」的檔案格式。`);
        return;
      }
      if (file.size > rule.maxBytes) {
        setError(`${file.name} 超過${rule.kind === 'image' ? '圖片 10 MB' : rule.kind === 'audio' ? '音訊 25 MB' : '影片 100 MB'}限制。`);
        return;
      }
    }

    setBusy(true);
    try {
      for (const [index, file] of selected.entries()) {
        const rule = getFileRule(file);
        if (!rule) throw new Error(`不支援「${file.name}」的檔案格式。`);
        const extension = safeExtension(file.name);
        const filePath = `${userId}/${eventId}/${crypto.randomUUID()}.${extension}`;

        setMessage(`正在上傳 ${index + 1}／${selected.length}：${file.name}`);

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(filePath, file, {
            contentType: rule.mime,
            upsert: false,
          });

        if (uploadError) throw new Error(`檔案上傳失敗：${uploadError.message}`);

        const { error: insertError } = await supabase
          .from('practice_event_media')
          .insert({
            event_id: eventId,
            user_id: userId,
            bucket_id: BUCKET,
            file_path: filePath,
            file_name: file.name,
            mime_type: rule.mime,
            media_type: rule.kind,
            size_bytes: file.size,
            description: description.trim() || null,
          });

        if (insertError) {
          await supabase.storage.from(BUCKET).remove([filePath]);
          throw new Error(`附件紀錄建立失敗：${insertError.message}`);
        }
      }

      setDescription('');
      if (inputRef.current) inputRef.current.value = '';
      await loadMedia();
      setMessage(`已安全儲存 ${selected.length} 個附件。`);
    } catch (uploadFailure) {
      setError(uploadFailure instanceof Error ? uploadFailure.message : '上傳失敗，請稍後再試。');
      setMessage(null);
    } finally {
      setBusy(false);
    }
  };

  const saveDescription = async (record: EvidenceMediaRecord) => {
    setBusy(true);
    setError(null);
    const { error: updateError } = await supabase
      .from('practice_event_media')
      .update({
        description: editingDescription.trim() || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', record.id)
      .eq('user_id', userId);

    if (updateError) {
      setError(`無法修改附件說明：${updateError.message}`);
    } else {
      setEditingId(null);
      await loadMedia();
      setMessage('附件說明已更新。');
    }
    setBusy(false);
  };

  const deleteMedia = async (record: EvidenceMediaRecord) => {
    if (!window.confirm(`確定刪除「${record.file_name}」？刪除後無法復原。`)) return;

    setBusy(true);
    setError(null);
    setMessage(null);

    const { error: storageError } = await supabase.storage
      .from(BUCKET)
      .remove([record.file_path]);

    if (storageError) {
      setError(`無法刪除檔案：${storageError.message}`);
      setBusy(false);
      return;
    }

    const { error: rowError } = await supabase
      .from('practice_event_media')
      .delete()
      .eq('id', record.id)
      .eq('user_id', userId);

    if (rowError) {
      setError(`檔案已移除，但附件紀錄清理失敗：${rowError.message}`);
    } else {
      await loadMedia();
      setMessage('附件已刪除。');
    }
    setBusy(false);
  };

  return (
    <section className="space-y-4 border-t border-foreground-200/50 pt-5">
      <div>
        <h3 className="text-sm font-medium text-foreground-800">Evidence 媒體附件</h3>
        <p className="mt-1 text-xs leading-relaxed text-foreground-500">
          檔案採私密儲存，只有你的登入帳號可以讀取。每筆最多 10 個、合計 200 MB。
        </p>
      </div>

      <div
        className={`rounded-lg border-2 border-dashed p-4 space-y-3 transition-colors ${dragging ? 'border-primary-500 bg-primary-50' : 'border-accent-300 bg-accent-50/30'}`}
        onDragEnter={(event) => { event.preventDefault(); if (!busy) setDragging(true); }}
        onDragOver={(event) => { event.preventDefault(); if (!busy) setDragging(true); }}
        onDragLeave={(event) => { event.preventDefault(); setDragging(false); }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!busy) void handleFiles(event.dataTransfer.files);
        }}
      >
        <input
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="附件說明（選填），例如：購買憑證、事件當時的錄音"
          maxLength={300}
          disabled={busy}
          className="w-full rounded-md border border-foreground-200 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder-foreground-400 focus:border-accent-300 focus:outline-none"
        />
        <input
          ref={inputRef}
          type="file"
          multiple
          disabled={busy}
          accept="image/*,audio/*,video/*,.aac,.m4a,.mp3,.wav,.ogg,.opus,.flac,.mp4,.webm,.mov,.mpeg,.mpg,.avi,.mkv,.3gp"
          onChange={(event) => handleFiles(event.target.files)}
          className="block w-full text-xs text-foreground-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-500 file:px-4 file:py-2 file:text-background-50 hover:file:bg-primary-600 disabled:opacity-60"
        />
        <p className="text-center text-xs font-medium text-primary-600">
          {dragging ? '放開滑鼠即可上傳' : '也可以把圖片、音訊或影片直接拖到這個框內'}
        </p>
        <p className="text-[11px] text-foreground-400">
          圖片 JPG／PNG／WEBP／GIF：每張 10 MB；音訊 MP3／WAV／M4A／AAC／OGG／OPUS／FLAC：25 MB；影片 MP4／WEBM／MOV／MPEG／AVI／MKV／3GP：100 MB。
        </p>
      </div>

      {message && <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700" role="status">{message}</p>}
      {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700" role="alert">{error}</p>}

      {records.length === 0 ? (
        <p className="rounded-md bg-foreground-50 px-3 py-4 text-center text-xs text-foreground-400">尚未上傳 Evidence 媒體。</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {records.map((record) => (
            <article key={record.id} className="overflow-hidden rounded-lg border border-foreground-200 bg-background-50">
              <MediaPreview record={record} />
              <div className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium text-foreground-700" title={record.file_name}>{record.file_name}</p>
                    <p className="text-[10px] text-foreground-400">{formatBytes(record.size_bytes)} · {mediaLabel(record.media_type)}</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => deleteMedia(record)}
                    className="shrink-0 text-xs text-rose-500 hover:text-rose-700 disabled:opacity-50"
                  >
                    刪除
                  </button>
                </div>

                {editingId === record.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingDescription}
                      onChange={(event) => setEditingDescription(event.target.value)}
                      maxLength={300}
                      rows={2}
                      className="w-full resize-y rounded-md border border-foreground-200 px-2 py-1.5 text-xs focus:border-accent-300 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button type="button" disabled={busy} onClick={() => saveDescription(record)} className="text-xs text-primary-600">儲存說明</button>
                      <button type="button" disabled={busy} onClick={() => setEditingId(null)} className="text-xs text-foreground-400">取消</button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingId(record.id);
                      setEditingDescription(record.description || '');
                    }}
                    className="block w-full text-left text-xs text-foreground-500 hover:text-primary-600"
                  >
                    {record.description || '＋ 新增附件說明'}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function MediaPreview({ record }: { record: EvidenceMediaRecord }) {
  if (!record.signedUrl) {
    return <div className="flex h-28 items-center justify-center bg-foreground-50 text-xs text-foreground-400">預覽網址暫時無法取得</div>;
  }
  if (record.media_type === 'image') {
    return <img src={record.signedUrl} alt={record.description || record.file_name} className="h-44 w-full object-contain bg-foreground-50" />;
  }
  if (record.media_type === 'audio') {
    return <div className="bg-foreground-50 p-3"><audio src={record.signedUrl} controls preload="metadata" className="w-full" /></div>;
  }
  return <video src={record.signedUrl} controls preload="metadata" className="h-52 w-full bg-black object-contain" />;
}

function safeExtension(fileName: string): string {
  const candidate = fileName.split('.').pop()?.toLowerCase() || 'bin';
  return /^[a-z0-9]{1,8}$/.test(candidate) ? candidate : 'bin';
}

function getFileRule(file: File): { mime: string; kind: MediaKind; maxBytes: number } | null {
  const byMime = allowedTypes[file.type.toLowerCase()];
  if (byMime) return { ...byMime, mime: file.type.toLowerCase() };

  const extension = safeExtension(file.name);
  return extensionTypes[extension] || null;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function mediaLabel(kind: MediaKind): string {
  return kind === 'image' ? '圖片' : kind === 'audio' ? '音訊' : '影片';
}
